import React from 'react';
import { BookOpen, Clock, ArrowRight } from 'lucide-react';

const ResourcesSection = ({ blogPosts, onTakeSelfAssessment }) => {
  return (
    <section id="resources" className="py-20 bg-white" data-testid="resources-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Mental Health Resources
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore articles, guides, and resources to support your mental health journey.
          </p>
        </div>

        {/* Blog Posts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {blogPosts.slice(0, 6).map((post, index) => (
            <div
              key={post.id}
              className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl transition-shadow duration-300"
              data-testid={`blog-card-${index}`}
            >
              {/* Image */}
              <div className="aspect-video overflow-hidden bg-gray-200">
                <img
                  src={post.image_url}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Category */}
                <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                  {post.category}
                </span>

                {/* Title */}
                <h3 className="text-xl font-bold text-gray-900 mt-4 mb-2 line-clamp-2">
                  {post.title}
                </h3>

                {/* Excerpt */}
                <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>

                {/* Meta */}
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <span>{post.author}</span>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{post.read_time} min read</span>
                  </div>
                </div>

                {/* Read More Link */}
                <button className="flex items-center gap-2 text-blue-600 font-medium hover:text-blue-700">
                  Read More <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Crisis Resources */}
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-red-600" />
            Crisis Resources
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <p className="font-semibold text-gray-900 mb-2">Suicide & Crisis Lifeline</p>
              <a href="tel:988" className="text-lg text-red-600 font-bold hover:underline">Call or Text 988</a>
              <p className="text-sm text-gray-600 mt-1">24/7 support</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-2">Crisis Text Line</p>
              <p className="text-lg text-red-600 font-bold">Text HOME to 741741</p>
              <p className="text-sm text-gray-600 mt-1">Free, 24/7 support</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-2">NAMI Helpline</p>
              <a href="tel:1-800-950-6264" className="text-lg text-red-600 font-bold hover:underline">1-800-950-NAMI</a>
              <p className="text-sm text-gray-600 mt-1">Mon-Fri, 10am-10pm ET</p>
            </div>
          </div>
        </div>

        {/* Self-Assessment CTA */}
        <div className="bg-blue-600 rounded-xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Not Sure Where to Start?</h3>
          <p className="text-lg mb-6 opacity-90">
            Take a confidential self-assessment to understand your mental health needs.
          </p>
          <button 
            onClick={onTakeSelfAssessment}
            className="bg-white text-blue-600 px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors font-semibold"
            data-testid="take-self-assessment-btn"
          >
            Take Self-Assessment
          </button>
        </div>
      </div>
    </section>
  );
};

export default ResourcesSection;